import axiosClient from "utils/axios";
import { ApiList, ApiOptionList, ApiResponse } from "types/api.types";
import {
  CreateEmployeeAdvanceRequest,
  DisburseEmployeeAdvanceRequest,
  EmployeeAdvance,
  RefundEmployeeAdvanceRequest,
  UpdateEmployeeAdvanceRequest,
} from "features/EmployeeAdvances/types/employee-advance.types";
import type {
  AdvanceSummaryData,
  AdvanceSummaryParams,
} from "components/common/AdvanceSummaryWidgets";

export const getEmployeeAdvances = async ({
  page = 1,
  limit = 10,
  search,
  status,
  employeeId,
}: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  employeeId?: number;
} = {}) => {
  try {
    const response = await axiosClient.post<ApiResponse<ApiList<EmployeeAdvance[]>>>(
      "/employee-advances/all",
      {
        page,
        limit,
        search: search || undefined,
        status: status || undefined,
        employeeId: employeeId || undefined,
      },
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching Employee Advance list : ", err);
    throw err;
  }
};

/** GET /employee-advances/summary — mirrors GET /payments/summary's query contract. */
export const getEmployeeAdvanceSummary = async (params: AdvanceSummaryParams) => {
  try {
    const response = await axiosClient.get<ApiResponse<AdvanceSummaryData>>(
      "/employee-advances/summary",
      { params },
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching Employee Advance summary : ", err);
    throw err;
  }
};

export const getEmployeeAdvanceOptions = async ({
  query,
  limit,
  page,
  status,
  ids,
  withCancelledDisbursement,
}: {
  query?: string;
  limit?: number;
  page?: number;
  status?: string;
  ids?: string;
  withCancelledDisbursement?: boolean;
}) => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiOptionList>>(
      `/employee-advances/options`,
      { params: { query, limit, page, status, ids, withCancelledDisbursement } },
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching Employee Advance Options : ", err);
    throw err;
  }
};

export const createEmployeeAdvance = async (payload: CreateEmployeeAdvanceRequest) => {
  try {
    const response = await axiosClient.post<ApiResponse<EmployeeAdvance>>(
      "/employee-advances",
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error creating Employee Advance : ", err);
    throw err;
  }
};

export const updateEmployeeAdvance = async (payload: UpdateEmployeeAdvanceRequest) => {
  try {
    const response = await axiosClient.put<ApiResponse<EmployeeAdvance>>(
      `/employee-advances`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error updating Employee Advance : ", err);
    throw err;
  }
};

export const getEmployeeAdvance = async (id: number) => {
  try {
    if (!Number.isInteger(id) || id <= 0) {
      throw new Error("Invalid ID");
    }
    const response = await axiosClient.post<ApiResponse<EmployeeAdvance>>(
      `/employee-advances/detail`,
      { id },
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching Employee Advance details : ", err);
    throw err;
  }
};

export const deleteEmployeeAdvance = async (id: number) => {
  try {
    const response = await axiosClient.delete<ApiResponse<EmployeeAdvance>>(
      `/employee-advances`,
      { data: { id } },
    );
    return response.data;
  } catch (err) {
    console.warn("Error deleting Employee Advance : ", err);
    throw err;
  }
};

export const cancelEmployeeAdvance = async (id: number, reason?: string | null) => {
  try {
    const response = await axiosClient.post<ApiResponse<EmployeeAdvance>>(
      `/employee-advances/${id}/cancel`,
      reason?.trim() ? { reason: reason.trim() } : {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error cancelling Employee Advance : ", err);
    throw err;
  }
};

/** DRAFT → PROCESSED — attaches the Payment. */
export const disburseEmployeeAdvance = async (
  id: number,
  payload: DisburseEmployeeAdvanceRequest,
) => {
  try {
    const response = await axiosClient.post<ApiResponse<EmployeeAdvance>>(
      `/employee-advances/${id}/disburse`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error disbursing Employee Advance : ", err);
    throw err;
  }
};

/** PROCESSED → REFUNDED; full remaining (un-settled) balance only, no partial refunds. */
export const refundEmployeeAdvance = async (
  id: number,
  payload: RefundEmployeeAdvanceRequest,
) => {
  try {
    const response = await axiosClient.post<ApiResponse<EmployeeAdvance>>(
      `/employee-advances/${id}/refund`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error refunding Employee Advance : ", err);
    throw err;
  }
};

/** Read-only, scoped list under an employee's own page — GET /employees/:employeeId/advances. */
export const getAdvancesByEmployee = async (employeeId: number) => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiList<EmployeeAdvance[]>>>(
      `/employees/${employeeId}/advances`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching employee advances : ", err);
    throw err;
  }
};
